import type { AuthUser } from '@/lib/auth';
import { getBillingAccess, resolveVerticalTenant } from '@/api/billing';

/** Path suffix required on external return_to URLs (vertical auth graft). */
export const EXTERNAL_LOGIN_CALLBACK_SUFFIX = '/auth/callback';

const DEFAULT_EXTERNAL_LOGIN_CLIENTS = [
  // sensarra: sensarra.com is the product SPA (landing + #/workbench bench);
  // app.sensarra.com is the Sensarra LAB authenticated product door
  // (app.hennipenny.com precedent). Two origins, ONE vertical — both resolve to sensarra, so no
  // shared-origin shadowing. Mirrors the registry's public_origins
  // (vertical_registry.py).
  {
    vertical: 'sensarra',
    origins: ['https://sensarra.com', 'https://www.sensarra.com', 'https://app.sensarra.com'],
  },
  { vertical: 'evidence-foundry', origins: ['https://app.evidencefoundry.com'] },
  // hennipenny: its own vertical (brand + billing) served over the SHARED Evidence
  // Foundry workspace (same rf-frontend service) but on its OWN app hostname
  // app.hennipenny.com (Model B) — a UNIQUE origin. Because origin=vertical again,
  // configuredClientForOrigin resolves it directly (no longer shadowed by
  // evidence-foundry), and its position in this list no longer matters.
  // workbench.hennipenny.com is the standalone NILM workbench app (repo
  // gcharris/hennipenny, Cloud Run `hennipenny-workbench`): a SECOND origin for the
  // SAME vertical — both resolve to hennipenny, so no shared-origin shadowing.
  {
    vertical: 'hennipenny',
    origins: ['https://app.hennipenny.com', 'https://workbench.hennipenny.com'],
  },
  // sakme: origins mirror the registry's public_origins (vertical_registry.py) —
  // sakme.ai beside sakme.app, two origins for ONE vertical. Added 2026-07-09 to
  // close the login-return drift before Sakme's web product launches — without it
  // a Sakme customer's post-login return falls through to the platform home.
  // (Interim manual add; the systemic fix is registry-derived, deferred behind
  // §1C.9's reconciler.)
  { vertical: 'sakme', origins: ['https://sakme.ai', 'https://sakme.app'] },
] as const;

function extraAllowedOrigins(): string[] {
  const raw = import.meta.env.VITE_EXTERNAL_LOGIN_RETURN_ORIGINS as string | undefined;
  if (!raw?.trim()) return [];
  return raw.split(',').map((entry) => entry.trim()).filter(Boolean);
}

function isLocalDevOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    return url.hostname === 'localhost' || url.hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

/**
 * Validate an external return_to URL and return its origin, or null when the URL
 * is not a well-formed callback we may hand a JWT back to. Origin allowlisting is
 * applied by callers via configuredVerticalsForOrigin.
 */
function allowedReturnOrigin(returnTo: string | null | undefined): string | null {
  if (!returnTo?.trim()) return null;
  let url: URL;
  try {
    url = new URL(returnTo);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (!url.pathname.endsWith(EXTERNAL_LOGIN_CALLBACK_SUFFIX)) return null;
  if (url.username || url.password) return null;
  if (url.search || url.hash) return null;
  return url.origin;
}

/**
 * Every vertical configured to be served at this origin, in declaration order.
 * A shared workspace origin (one hostname serving multiple verticals) yields more
 * than one entry; a unique origin — which every configured vertical now has —
 * yields exactly one. The first entry is the origin's default when no signed
 * vertical_id disambiguates.
 */
function configuredVerticalsForOrigin(origin: string): string[] {
  const matches = DEFAULT_EXTERNAL_LOGIN_CLIENTS
    .filter((client) => (client.origins as readonly string[]).includes(origin))
    .map((client) => client.vertical);
  if (matches.length > 0) return matches;
  if (extraAllowedOrigins().includes(origin)) return ['external'];
  if (isLocalDevOrigin(origin)) return ['sensarra'];
  return [];
}

function configuredClientForOrigin(origin: string): string | null {
  return configuredVerticalsForOrigin(origin)[0] ?? null;
}

/**
 * Return the vertical key for an absolute callback URL we may hand a JWT back to,
 * derived from ORIGIN alone (first-match). Correct for every unique-origin
 * vertical; for a shared workspace origin it returns the origin default and
 * CANNOT disambiguate co-tenant verticals — use resolveExternalLoginVertical
 * with the signed vertical_id for that.
 */
export function externalLoginVertical(returnTo: string | null | undefined): string | null {
  const origin = allowedReturnOrigin(returnTo);
  return origin === null ? null : configuredClientForOrigin(origin);
}

/**
 * Resolve the vertical for an external login return, preferring a vertical_id we
 * SIGNED over origin-derivation. This is the disambiguation for shared workspace
 * origins — one hostname serving more than one vertical — where origin alone always
 * resolves to the first entry and shadows the rest. (HenniPenny once shared
 * app.evidencefoundry.com with Evidence Foundry; Model B gave it its own
 * app.hennipenny.com origin, so no configured vertical is shadowed today — but the
 * signed-id preference stays correct for any future co-tenant origin.)
 *
 * Trust boundary: `signedVerticalId` MUST come from a value WE signed — the
 * enrollment token's vertical_id (verified server-side and echoed back by
 * enrollFirstPasskey) or our own JWT — NEVER a raw query param or other
 * client-supplied field. It is honored ONLY when it names a vertical actually
 * configured for the validated return origin, so it can disambiguate among the
 * verticals legitimately served at that origin but can never redirect a JWT to
 * an unrelated vertical/origin. When absent or unrecognized we fall back to the
 * origin default, leaving every unique-origin vertical byte-identical.
 */
export function resolveExternalLoginVertical(
  returnTo: string | null | undefined,
  signedVerticalId?: string | null,
): string | null {
  const origin = allowedReturnOrigin(returnTo);
  if (origin === null) return null;
  const verticals = configuredVerticalsForOrigin(origin);
  if (verticals.length === 0) return null;
  const signed = signedVerticalId?.trim();
  if (signed && verticals.includes(signed)) return signed;
  return verticals[0];
}

/** True when return_to is an absolute callback URL we may hand a JWT back to. */
export function isAllowedExternalLoginReturnTo(returnTo: string | null | undefined): boolean {
  return externalLoginVertical(returnTo) !== null;
}

export function externalUserPayload(user: AuthUser): Record<string, unknown> {
  return {
    uid: user.uid,
    email: user.email,
    name: user.name ?? null,
    tenantId: user.tenantId ?? null,
    engineUserId: user.engineUserId ?? null,
    vertical: user.activeVertical ?? null,
  };
}

const ALLOWED_CUSTOMER_TENANT_STATUSES = new Set(['active', 'subscribed']);

export function validateExternalCustomerAccess(user: AuthUser, vertical: string): void {
  if (!user.tenantId) {
    throw new Error(`No active tenant is available for ${vertical}. Ask the operator to finish approval first.`);
  }

  const status = user.tenant?.status?.trim().toLowerCase();
  if (status && !ALLOWED_CUSTOMER_TENANT_STATUSES.has(status)) {
    throw new Error(`This tenant is ${status}. Access opens after the tenant is active.`);
  }
}

export async function validateExternalCustomerSubscriptionAccess(
  user: AuthUser,
  vertical: string,
): Promise<void> {
  validateExternalCustomerAccess(user, vertical);

  // The backend /access endpoint is the single source of truth for the admit
  // rule (active-only, keyed on (tenant, vertical)); we no longer re-derive it.
  const decision = await getBillingAccess(vertical);
  if (!decision.admitted) {
    const status = decision.subscription_status?.trim().toLowerCase() || 'unknown';
    throw new Error(
      `No active ${vertical} subscription is available. Current subscription status: ${status}.`,
    );
  }
}

/**
 * Resolve the tenant a vertical door hands to its app — EXPLICIT tenant
 * selection (HENNIPENNY-LAB-ACTIVATION-1). The engine's default active-tenant
 * resolution prefers OWNER memberships, so a lab member who also owns a
 * personal tenant would land in the personal tenant; the backend resolver
 * instead picks the membership that actually holds the vertical's billing
 * admission (validated server-side against real engine membership). No
 * admitted membership → an honest rejection, never a personal-tenant fallback.
 */
export async function resolveExternalCustomerTenant(vertical: string): Promise<string> {
  const resolution = await resolveVerticalTenant(vertical);
  if (!resolution.admitted || !resolution.tenant_id) {
    throw new Error(
      `No ${vertical} access is available on this account (${resolution.reason}). ` +
        'Ask the operator to finish approval, or apply from the product site.',
    );
  }
  return resolution.tenant_id;
}

/**
 * Fragment handoff contract paired with research-hub PR #49.
 * `activeTenantId` (the ADMITTED tenant slug from resolveExternalCustomerTenant)
 * rides in the fragment as `tenant` and replaces the engine-default `tenantId`
 * in the user payload, so the consuming app ends the callback knowing exactly
 * which tenant it acts in.
 */
export function buildExternalLoginRedirectUrl(
  returnTo: string,
  token: string,
  user: AuthUser,
  vertical = externalLoginVertical(returnTo) ?? 'external',
  activeTenantId?: string | null,
): string {
  const verticalUser = {
    ...user,
    activeVertical: vertical,
    ...(activeTenantId ? { tenantId: activeTenantId } : {}),
  };
  const params = new URLSearchParams();
  params.set('token', token);
  params.set('user', JSON.stringify(externalUserPayload(verticalUser)));
  params.set('vertical', vertical);
  if (activeTenantId) params.set('tenant', activeTenantId);
  return `${returnTo}#${params.toString()}`;
}
